import React, { useContext, useState } from "react";
import {
  BrandTheme,
  FeedbackProps,
  FormDataItem,
  QuestThemeData,
} from "../types/SurveyTypes";
import ThankYouPopup from "./ThankYouPopup";
import FormNavigation from "./FormNavigation";
import QuestionSections from "./QuestionSection";
import TopBarSection from "./TopBarSection";
import QuestLabs from "../../../../components/QuestLabs";
import { useThemeManager } from "./useThemeManager";
import QuestContext from "../../../../components/QuestWrapper";
import General from "../../../../general";
import '../../../../components/Modules/css/input.css';
import './Feedback.css'

interface OfflineSurveyProps {
  sections?: any;
  ratingType?: string;
  iconColor?: string;
  showFooter?: boolean;
  styleConfig?: any;
  singleChoiceModal?: string;
  multiChoiceModal?: string;
  formdata: FormDataItem[];
  design: number[][];
  brandTheme: BrandTheme;
  questThemeData?: QuestThemeData;
  Headers?: any;
  onSubmit?: (data: any) => void;
  itemPerPage?:number
}

interface ValidationState {
  [key: string]: {
    isValid: boolean;
    regexValid: boolean;
  };
}

const SurveyOffline: React.FC<OfflineSurveyProps> = ({
  sections,
  ratingType = "number",
  iconColor = "#939393",
  showFooter = true,
  styleConfig = {},
  singleChoiceModal,
  multiChoiceModal,
  formdata,
  design = [],
  brandTheme,
  questThemeData,
  Headers,
  onSubmit,
}) => {
  const [thanksPopup, setThanksPopup] = useState(false);
  const [answer, setAnswer] = useState<Record<string, string | string[]>>({});
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, Array<string>>
  >({});
  const [validationState, setValidationState] = useState<ValidationState>({});
  const [goToNextSection, setGoToNextSection] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [sectionQuestions, setSectionQuestions] = useState<FormDataItem[]>([]);
  const GeneralFunctions = new General("mixpanel", "Offline");
  const { themeConfig } = useContext(
    QuestContext.Context
  );

  const defaultEmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const themeManager = useThemeManager(
    brandTheme,
    styleConfig,
    themeConfig,
    questThemeData
  );

  const validateField = (questionId: string, value: any, question: any) => {
    let isValid = true;
    let regexValid = true;

    if (question.required) {
      switch (question.type) {
        case "USER_INPUT_MULTI_CHOICE":
          isValid = selectedOptions[questionId]?.length > 0;
          break;
        case "USER_INPUT_SINGLE_CHOICE":
          isValid = !!answer[questionId];
          break;
        case "RATING":
          isValid =
            answer[questionId] !== undefined && answer[questionId] !== "";
          break;
        case "USER_INPUT_TEXT":
        case "USER_INPUT_EMAIL":
        case "USER_INPUT_TEXTAREA":
        case "USER_INPUT_DATE":
        case "USER_INPUT_PHONE":
        case "USER_INPUT_NUMBER":
          isValid = value !== undefined && value !== "";
          break;
        default:
          isValid = !!value;
      }
    }

    if (question.type === "USER_INPUT_EMAIL" && value) {
      if (question.regexKey) {
        try {
          const regex = new RegExp(question.regexKey);
          regexValid = regex.test(value.toString());
        } catch (e) {
          regexValid = defaultEmailPattern.test(value.toString());
        }
      } else {
        regexValid = defaultEmailPattern.test(value.toString());
      }
    } else if (question.regexKey && value) {
      try {
        const regex = new RegExp(question.regexKey);
        regexValid = regex.test(value.toString());
      } catch (e) {
        regexValid = true;
      }
    }

    return { isValid, regexValid };
  };

  const updateValidationState = () => {
    if (sectionQuestions) {
      const newValidationState: ValidationState = {};

      sectionQuestions.forEach((question: any) => {
        const value = answer[question.actionId];
        const validation = validateField(question.actionId, value, question);
        newValidationState[question.actionId] = validation;
      });

      setValidationState(newValidationState);
      const isSectionValid = Object.values(newValidationState).every(
        (state) => state.isValid && state.regexValid
      );
      setGoToNextSection(isSectionValid);
    }
  };

  const validateInputRegex = (validated: boolean, actionId: string) => {
    if (!validationState[actionId]) return;

    setValidationState((prev) => ({
      ...prev,
      [actionId]: {
        ...prev[actionId],
        regexValid: validated,
      },
    }));

    const isAllValid = Object.values({
      ...validationState,
      [actionId]: {
        ...validationState[actionId],
        regexValid: validated,
      },
    }).every((state) => state.isValid && state.regexValid);

    setGoToNextSection(isAllValid);
  };

  const handleRatingChange = (id: string, newRating: number) => {
    setAnswer({
      ...answer,
      [id]: String(newRating),
    });
  };

  const handleCheckboxChange = (option: string, actionId: string): void => {
    setSelectedOptions((prev) => {
      const currentOptions = prev[actionId] || [];
      if (currentOptions.includes(option)) {
        return {
          ...prev,
          [actionId]: currentOptions.filter((e) => e !== option),
        };
      }
      return {
        ...prev,
        [actionId]: [...currentOptions, option],
      };
    });
  };

  const handleUpdate = (e: any, id: string, j: string) => {
    if (e.target.checked === true && j === "check") {
      let ans = Array.isArray(answer[id]) ? [...(answer[id] as string[])] : [];
      ans.push(e.target.value);
      setAnswer({
        ...answer,
        [id]: ans,
      });
    } else if (
      e.target.checked === false &&
      Array.isArray(answer[id]) &&
      j === "check"
    ) {
      let ans = answer[id] as string[];
      let mod_ans = ans.filter((an) => an !== e.target.value);
      setAnswer({
        ...answer,
        [id]: mod_ans,
      });
    } else {
      setAnswer({
        ...answer,
        [id]: e.target.value,
      });
    }
  };

  const handleRadioChange = (option: string, actionId: string, j: string) => {
    if (j === "radio") {
      setAnswer((prev: any) => ({
        ...prev,
        [actionId]: prev[actionId]?.includes(option) ? [] : [option],
      }));
    } else {
      setAnswer((prev: any) => ({
        ...prev,
        [actionId]: option,
      }));
    }
  };

  const handleReturnAnswers = () => {
    if (onSubmit) {
      onSubmit({ answer, selectedOptions });
    }
    setThanksPopup(true);
  };

  const divideQuestionInSection = (formdata: FormDataItem[]) => {
    let questionsDataArray: FormDataItem[] = [];
    if (design[currentSection]) {
      design[currentSection].forEach((questioNo) => {
        const questionData = formdata[questioNo - 1];
        if (questionData) {
          questionsDataArray.push(questionData);
        }
      });
    }
    return questionsDataArray;
  };

  React.useEffect(() => {
    if (
      design?.length > 0 &&
      formdata.length > 0 &&
      currentSection >= 0 &&
      design[currentSection]?.length > 0
    ) {
      const sectionQuestions = divideQuestionInSection(formdata);
      setSectionQuestions(sectionQuestions);
    }
  }, [design, formdata, currentSection]);

  React.useEffect(() => {
    updateValidationState();
  }, [answer, selectedOptions, currentSection, sectionQuestions]);


  if(formdata.length == 0 ) return null

  return (
    <div style={themeManager.getFormStyles()} className="q-feedback-cont">
      {formdata.length > 0 && (
        <>
          {!thanksPopup && (
            <div>
              <TopBarSection
                Headers={Headers}
                sections={sections}
                page={currentSection}
                themeManager={themeManager}
              />
              <form
                style={{
                  padding: "20px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                {sectionQuestions?.map((data: any, index: number) => (
                  <QuestionSections
                    key={data?.criteriaId || index}
                    data={data}
                    answer={answer}
                    handleUpdate={handleUpdate}
                    handleRadioChange={handleRadioChange}
                    handleRatingChange={handleRatingChange}
                    selectedOptions={selectedOptions}
                    handleCheckboxChange={handleCheckboxChange}
                    ratingType={ratingType}
                    sections={sections}
                    themeManager={themeManager}
                    singleChoiceModal={singleChoiceModal}
                    multiChoiceModal={multiChoiceModal}
                    validateInputRegex={validateInputRegex}
                  />
                ))}
                <FormNavigation
                  currentSection={currentSection}
                  setCurrentSection={setCurrentSection}
                  design={design}
                  returnAnswers={handleReturnAnswers}
                  goToNextSection={goToNextSection}
                  themeManager={themeManager}
                  GeneralFunctions={GeneralFunctions}
                />
              </form>
              {showFooter && (
                <QuestLabs style={themeManager.getFooterStyles()} />
              )}
            </div>
          )}
          {thanksPopup && (
            <ThankYouPopup
              handleThanks={() => setThanksPopup(false)}
              showFooter={showFooter}
              iconColor={iconColor}
              themeManager={themeManager}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SurveyOffline;
